import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Coins, Bell, Menu, LogOut, LayoutDashboard, Shield, Library, Compass, Wallet as WalletIcon, X, Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Header() {
  const { user, coins, logout, region, regions, setRegionCurrency } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user) {
      api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    }
  }, [user, loc.pathname]);

  const links = [{ to: "/", label: "Discover", icon: Compass }];
  if (user) links.push({ to: "/library", label: "Library", icon: Library });
  if (user && (user.role === "CREATOR" || user.role === "ADMIN"))
    links.push({ to: "/creator", label: "Studio", icon: LayoutDashboard });
  if (user && (user.role === "MODERATOR" || user.role === "ADMIN"))
    links.push({ to: "/moderation", label: "Moderation", icon: Shield });
  if (user && user.role === "ADMIN") links.push({ to: "/admin", label: "Admin", icon: Shield });

  const NavLinks = ({ onClick }) =>
    links.map((l) => {
      const Active = loc.pathname === l.to;
      return (
        <Link
          key={l.to}
          to={l.to}
          onClick={onClick}
          data-testid={`nav-${l.label.toLowerCase()}`}
          className={`text-sm tracking-wide transition-colors duration-300 flex items-center gap-2 ${
            Active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <l.icon size={16} strokeWidth={1.5} />
          {l.label}
        </Link>
      );
    });

  return (
    <header className="glass border-b sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="logo" className="font-display text-xl tracking-tight">
            Whisper<span className="text-gold">Tales</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <NavLinks />
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <Select value={region} onValueChange={setRegionCurrency}>
            <SelectTrigger data-testid="region-select" className="h-9 w-auto gap-1.5 border-white/10 bg-black/30 text-xs px-3 hover:bg-white/5 transition-colors duration-300">
              <Globe size={14} strokeWidth={1.5} className="text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r.code} value={r.code} data-testid={`region-option-${r.code}`}>
                  {r.label} · {r.currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user ? (
            <>
              <Link
                to="/wallet"
                data-testid="wallet-balance"
                className="flex items-center gap-1.5 rounded-full border border-gold/40 bg-black/30 px-3 py-1.5 text-sm hover:bg-gold/10 transition-colors duration-300"
              >
                <Coins size={16} className="text-gold" strokeWidth={1.5} />
                <span className="text-gold font-semibold tabular-nums">{coins}</span>
              </Link>
              <Link to="/notifications" data-testid="nav-notifications" className="relative p-2 text-muted-foreground hover:text-foreground transition-colors duration-300">
                <Bell size={18} strokeWidth={1.5} />
                {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />}
              </Link>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="icon"
                onClick={() => { logout(); nav("/"); }}
                className="hidden md:inline-flex hover:bg-white/5"
              >
                <LogOut size={18} strokeWidth={1.5} />
              </Button>
            </>
          ) : (
            <Button
              data-testid="header-signin"
              onClick={() => nav("/auth")}
              className="bg-primary hover:bg-primary/90 text-primary-foreground transition-colors duration-300"
            >
              Sign in
            </Button>
          )}
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden glass border-t px-6 py-4 flex flex-col gap-4">
          <NavLinks onClick={() => setOpen(false)} />
          {user && (
            <button
              data-testid="mobile-logout"
              onClick={() => { logout(); setOpen(false); nav("/"); }}
              className="text-sm text-muted-foreground flex items-center gap-2"
            >
              <LogOut size={16} /> Sign out
            </button>
          )}
        </div>
      )}
    </header>
  );
}
